/** Severity levels in ascending order of importance. */
export type LogLevel = "trace" | "debug" | "info" | "warn" | "error" | "silent";

/** A single structured log record emitted by any namespaced logger. */
export type LogRecord = {
  ts: string;
  level: Exclude<LogLevel, "silent">;
  ns: string;
  message: string;
  data?: unknown;
};

export type Logger = Record<
  Exclude<LogLevel, "silent">,
  (message: string, data?: unknown) => void
>;

const ORDER: Record<LogLevel, number> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  silent: 99,
};
const CONSOLE: Record<
  Exclude<LogLevel, "silent">,
  "debug" | "info" | "warn" | "error"
> = {
  trace: "debug",
  debug: "debug",
  info: "info",
  warn: "warn",
  error: "error",
};

let activeLevel: LogLevel = "debug";
const subscribers = new Set<(record: LogRecord) => void>();
const VALID_LEVELS = new Set<string>([
  "trace",
  "debug",
  "info",
  "warn",
  "error",
  "silent",
]);

/**
 * Sets the global minimum level; records below it are dropped. Validated here rather than only
 * at the URL/storage boundary: this is the single writer of the active level, and an unchecked
 * assignment (`"constructor"`, `"__proto__"`, a typo) would make `ORDER[activeLevel]` resolve to
 * a prototype member and silence or unsilence every logger. An invalid level is ignored, warned
 * about, and leaves the current level untouched.
 */
export function setLogLevel(level: LogLevel): void {
  if (!VALID_LEVELS.has(level)) {
    emit("logger", "warn", "ignoring invalid log level", {
      level,
      valid: [...VALID_LEVELS],
    });
    return;
  }
  activeLevel = level;
}

/** Returns the global minimum level. */
export function getLogLevel(): LogLevel {
  return activeLevel;
}

/** Registers a log sink. Returns an unsubscribe function. */
export function onLogRecord(cb: (record: LogRecord) => void): () => void {
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

/** Reads the initial level from `?logLevel=` then localStorage, defaulting to info. */
export function initLogLevelFromEnvironment(
  search: string,
  storage: Pick<Storage, "getItem">,
): void {
  const fromUrl = new URLSearchParams(search).get("logLevel");
  const candidate = fromUrl ?? storage.getItem("cawd.logLevel") ?? "debug";
  if (VALID_LEVELS.has(candidate)) setLogLevel(candidate as LogLevel);
}

function emit(
  ns: string,
  level: Exclude<LogLevel, "silent">,
  message: string,
  data?: unknown,
): void {
  if (ORDER[level] < ORDER[activeLevel]) return;
  const record: LogRecord = {
    ts: new Date().toISOString(),
    level,
    ns,
    message,
    ...(data !== undefined && { data }),
  };
  // eslint-disable-next-line no-console -- the logger is the single sanctioned console boundary
  console[CONSOLE[level]](`[${ns}] ${message}`, consoleSafe(data));
  writeBootLog(record);
  for (const cb of subscribers) {
    try {
      cb(record);
    } catch {
      /* a broken sink must never break the caller */
    }
  }
}

/** Creates a logger bound to a namespace. */
export function createLogger(ns: string): Logger {
  return {
    trace: (m, d) => emit(ns, "trace", m, d),
    debug: (m, d) => emit(ns, "debug", m, d),
    info: (m, d) => emit(ns, "info", m, d),
    warn: (m, d) => emit(ns, "warn", m, d),
    error: (m, d) => emit(ns, "error", m, d),
  };
}

/**
 * JSON.stringify throws on circular SDK objects and can freeze DevTools. Console and the
 * boot log get a bounded stand-in instead; the EventLog keeps the original value.
 */
function consoleSafe(data: unknown): unknown {
  if (data === undefined) return "";
  try {
    const flat = JSON.stringify(data);
    return flat.length > 2000 ? `${flat.slice(0, 1999)}…` : data;
  } catch {
    return "[unserializable]";
  }
}

/** Appends to `#boot-log` so a React crash still leaves a visible trail in the iframe. */
function writeBootLog(record: LogRecord): void {
  if (typeof document === "undefined") return;
  const el = document.getElementById("boot-log");
  if (!el) return;
  el.hidden = false;
  const line = `${record.ts} ${record.level} [${record.ns}] ${record.message}`;
  el.textContent = `${el.textContent ?? ""}${line}\n`;
}

/** Hides the pre-React boot log once the app shell has mounted. */
export function hideBootLog(): void {
  if (typeof document === "undefined") return;
  const el = document.getElementById("boot-log");
  if (el) el.hidden = true;
}
